import React, { useEffect, useRef, useState } from 'react';
import { authAPI } from './api';
import './AuthScreen.css';

const SITE_URL = typeof window !== 'undefined' ? window.location.origin : 'https://efinpro.triyoginnovation.com';
const SEO_TITLE = 'e-FinPro | GST Invoice & Contract Management SaaS for NGOs and PMUs in India';
const SEO_DESC = 'e-FinPro is India\'s contract-to-cash SaaS — manage agreements, raise GST invoices, track TDS receipts, run a bank ledger, and export Tally-ready reports from one workspace. Free to start.';
const SEO_KEYS = 'GST invoice software NGO India, contract management TDS tracking, PMU billing software India, NGO finance management software, government work order billing, Tally XML export invoice, multi-currency invoice software India, donor-funded programme billing, consulting firm TDS receipt software, contract-to-cash SaaS India, audit-ready financial reports NGO';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const DEFAULT_AUTH_CONFIG = {
  registrationOpen: false,
  turnstileEnabled: false,
  turnstileSiteKey: '',
  passwordPolicy: {
    minLength: 12,
    requiresUppercase: true,
    requiresLowercase: true,
    requiresNumber: true,
  },
};

let turnstileScriptPromise = null;

const buildPasswordPolicyText = (policy = DEFAULT_AUTH_CONFIG.passwordPolicy) =>
  `Password must be at least ${policy.minLength} characters and include uppercase, lowercase, and a number`;

const hasStrongPassword = (password, policy = DEFAULT_AUTH_CONFIG.passwordPolicy) => {
  const value = String(password || '');
  return value.length >= (policy.minLength || 12)
    && (!policy.requiresLowercase || /[a-z]/.test(value))
    && (!policy.requiresUppercase || /[A-Z]/.test(value))
    && (!policy.requiresNumber || /\d/.test(value));
};

const loadTurnstileScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

const injectMeta = (attr, key, val) => {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', val);
};

const injectCanonical = (href) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

const injectLD = (data) => {
  let script = document.getElementById('efinpro-ld');
  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'efinpro-ld';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
};

const STATS = [
  { num: '6', label: 'Integrated modules' },
  { num: '5', label: 'Export formats' },
  { num: '100%', label: 'India tax compliant' },
  { num: 'Rs 0', label: 'To get started' },
];

const HERO_SIGNALS = [
  { value: '2 min', label: 'firm setup' },
  { value: 'GST + TDS', label: 'finance controls' },
  { value: '5 exports', label: 'audit-ready outputs' },
];

const HERO_MODULES = [
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17H7A5 5 0 0 1 7 7h2" /><path d="M15 7h2a5 5 0 0 1 0 10h-2" /><line x1="8" y1="12" x2="16" y2="12" /></svg>,
    cls: 'fi-navy',
    title: 'Contract control',
    body: 'Keep scopes, milestones, deliverables, and amendments tied to billable value before finance work starts.',
    kicker: 'Scope and billing stay connected',
  },
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
    cls: 'fi-amber',
    title: 'Invoice without re-entry',
    body: 'Generate GST-ready invoices with numbering, signatories, currency handling, and export structure already in place.',
    kicker: 'Built for IGST, CGST, and exports',
  },
  {
    icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /><line x1="12" y1="11" x2="12" y2="17" /></svg>,
    cls: 'fi-green',
    title: 'Receipts that reconcile',
    body: 'Capture settlements, TDS deductions, and bank impact in one pass so reports do not depend on spreadsheet rebuilds.',
    kicker: 'Live status from invoice to receipt',
  },
];

const FEATURES = [
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17H7A5 5 0 0 1 7 7h2" /><path d="M15 7h2a5 5 0 0 1 0 10h-2" /><line x1="8" y1="12" x2="16" y2="12" /></svg>,
    cls: 'fi-navy',
    title: 'Contract Workspace',
    body: 'Maintain client agreements with milestones, deliverables, amendment trails, and document registers — every field linked to billing.',
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>,
    cls: 'fi-green',
    title: 'GST Invoice Engine',
    body: 'Raise IGST/CGST-compliant invoices with auto-sequential numbering, multi-currency support, and authorised signatory on every invoice.',
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    cls: 'fi-amber',
    title: 'TDS-Aware Receipts',
    body: 'Record every payment with client-deducted TDS amounts, net settlement values, and full reconciliation status against raised invoices.',
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>,
    cls: 'fi-purple',
    title: 'Bank Ledger',
    body: 'Opening balance + invoice credits − expense debits = live running bank balance. Every entry is timestamped and auditable.',
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    cls: 'fi-teal',
    title: '5 Export Formats',
    body: 'Invoice register, GST summary, TDS report, bank ledger CSV, and Tally Sales Voucher XML — all filterable by date or fiscal year.',
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
    cls: 'fi-red',
    title: 'Multi-Firm Access',
    body: 'Each firm workspace is fully isolated. Add team members, admin roles, and authorised signatories with a complete activity audit trail.',
  },
];

const WORKFLOW = [
  { n: '01', title: 'Create Contract', body: 'Capture client, billing, and agreement metadata with country, currency, and GST settings.' },
  { n: '02', title: 'Build Invoices', body: 'Generate GST invoices from contract — client details, signatory, and prefix auto-fill.' },
  { n: '03', title: 'Record Receipts', body: 'Log payments with TDS deductions and settlement status against each invoice.' },
  { n: '04', title: 'Export & Report', body: 'Download invoice, GST, TDS, ledger, and Tally-ready files for finance and audit.' },
];

const INDUSTRIES = [
  { icon: '🏛️', title: 'NGOs & Donor-Funded Programmes', body: 'Manage grant-linked contracts, donor billing references, milestone tracking, and receivable reconciliation from one workspace.' },
  { icon: '🏗️', title: 'Government PMUs & Public Sector', body: 'Handle multi-vendor agreements, compliance documentation, amendment trails, and structured billing against government work orders.' },
  { icon: '📐', title: 'Consulting Firms & Consortiums', body: 'Track project-based billing, retainer agreements, milestone invoicing, and TDS-adjusted receipts across client engagements.' },
];

const FAQS = [
  { q: 'Who is e-FinPro built for?', a: 'Teams managing contract-led finance operations — NGOs, PMUs, public sector missions, and consulting firms where each invoice must trace back to a contract and every receipt must record TDS.' },
  { q: 'Does it handle Indian tax requirements?', a: 'Yes. GST invoicing (IGST/CGST), TDS deduction tracking, settlement recording, and Tally XML export are core to the platform. GST can be toggled off per contract for international billing.' },
  { q: 'Does e-FinPro support multi-currency billing?', a: 'Yes. Each contract can be set to any currency — INR, USD, EUR, GBP, AED, SGD, and others. GST is applied only to INR contracts by default and can be toggled off independently.' },
  { q: 'How does TDS deduction tracking work?', a: 'When recording a payment receipt, enter the TDS rate and deducted amount separately from the net settlement. e-FinPro records both the gross invoice amount and the TDS component, and marks the invoice as settled or partially settled accordingly.' },
  { q: 'Can multiple firms use the same platform?', a: 'Yes. Each firm has a fully isolated workspace. Admins add team members and signatories. No firm can access another\'s contracts, invoices, or financial data.' },
  { q: 'Can I add multiple signatories?', a: 'Yes. Add as many authorised signatories as needed from Settings, each with a digital signature image and title. Set one as the default — it auto-selects on every new invoice.' },
  { q: 'How do we export to Tally?', a: 'From the Reports menu, download a Sales Voucher XML file formatted for Tally ERP import — one voucher per invoice with debtor ledger, sales ledger, and IGST entries ready for direct import.' },
  { q: 'Is there a free trial?', a: 'Register your firm directly — no credit card required. Start with contracts and invoices, then expand into receipts, ledger, and reporting at your own pace.' },
];

const CONTACT_POINTS = [
  'Onboarding guidance for NGOs, PMUs, and consulting firms',
  'Help with GST, TDS, signatories, and first-report setup',
  'Fast product answers before you invite your full team',
];

function AuthForm({ onAuth, authConfig, authIntent }) {
  const cardRef = useRef(null);
  const [tab, setTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [regData, setRegData] = useState({ firmName: '', firmEmail: '', adminName: '', adminEmail: '', password: '', confirmPassword: '' });

  const setL = (key, value) => setLoginData((prev) => ({ ...prev, [key]: value }));
  const setR = (key, value) => setRegData((prev) => ({ ...prev, [key]: value }));

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setError('');
    setStep(1);
    setCaptchaToken('');
    setCaptchaResetKey((value) => value + 1);
  };

  useEffect(() => {
    if (!authIntent?.version) return;

    setTab(authIntent.tab || 'login');
    setStep(authIntent.step || 1);
    setError('');
    setCaptchaToken('');
    setCaptchaResetKey((value) => value + 1);

    const focusTarget = () => {
      const selector = authIntent.tab === 'register'
        ? (authIntent.step === 2 ? 'input[placeholder="e.g. Triyog Innovations Pvt Ltd"]' : 'input[placeholder="e.g. N. S. Mehta"]')
        : 'input[type="email"]';
      cardRef.current?.querySelector(selector)?.focus();
    };

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(focusTarget);
    } else {
      focusTarget();
    }
  }, [authIntent]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await authAPI.login({ email: loginData.email, password: loginData.password });
      localStorage.setItem('ti_token', data.token);
      onAuth(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setError('');

    if (step === 1) {
      if (regData.password !== regData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      if (!hasStrongPassword(regData.password, authConfig.passwordPolicy)) {
        setError(buildPasswordPolicyText(authConfig.passwordPolicy));
        return;
      }
      setStep(2);
      return;
    }
    if (!authConfig.registrationOpen) {
      setError('Secure registration is temporarily unavailable until CAPTCHA keys are configured.');
      return;
    }
    if (authConfig.turnstileEnabled && !captchaToken) {
      setError('Please complete the security verification before creating your firm.');
      return;
    }

    setLoading(true);
    try {
      const data = await authAPI.register({
        firmName: regData.firmName,
        firmEmail: regData.firmEmail || regData.adminEmail,
        adminName: regData.adminName,
        adminEmail: regData.adminEmail,
        password: regData.password,
        turnstileToken: captchaToken,
      });
      localStorage.setItem('ti_token', data.token);
      onAuth(data);
    } catch (err) {
      setError(err.message);
      setCaptchaToken('');
      setCaptchaResetKey((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-auth-card" ref={cardRef}>
      <div className="lp-auth-tabs">
        <button className={`lp-auth-tab${tab === 'login' ? ' active' : ''}`} onClick={() => switchTab('login')}>Sign In</button>
        <button className={`lp-auth-tab${tab === 'register' ? ' active' : ''}`} onClick={() => switchTab('register')}>Register Free</button>
      </div>

      <div className="lp-auth-body">
        {tab === 'login' && <><div className="lp-auth-title">Welcome back</div><div className="lp-auth-subtitle">Sign in to your firm workspace</div></>}
        {tab === 'register' && step === 1 && <><div className="lp-auth-title">Create your account</div><div className="lp-auth-subtitle">Step 1 of 2 — your admin credentials</div></>}
        {tab === 'register' && step === 2 && <><div className="lp-auth-title">Your firm details</div><div className="lp-auth-subtitle">Step 2 of 2 — we'll set up your workspace</div></>}

        {tab === 'register' && (
          <div className="lp-steps">
            <div className={`lp-step ${step >= 1 ? 'done' : ''}`}><span className="lp-step-num">1</span><span>Account</span></div>
            <div className="lp-step-line" />
            <div className={`lp-step ${step >= 2 ? 'done' : ''}`}><span className="lp-step-num">2</span><span>Firm</span></div>
          </div>
        )}

        {error && <div className="lp-auth-error">{error}</div>}

        {tab === 'login' && (
          <form onSubmit={handleLogin} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Email</label><input className="lp-inp" type="email" required autoFocus value={loginData.email} onChange={(event) => setL('email', event.target.value)} placeholder="you@firm.com" /></div>
            <div className="lp-fg"><label className="lp-lbl">Password</label><input className="lp-inp" type="password" required value={loginData.password} onChange={(event) => setL('password', event.target.value)} placeholder="••••••••" /></div>
            <button className="lp-submit" type="submit" disabled={loading}>{loading ? 'Signing in…' : 'Sign In →'}</button>
            <p className="lp-auth-switch">No account? <button type="button" onClick={() => switchTab('register')}>Register your firm free</button></p>
          </form>
        )}

        {tab === 'register' && step === 1 && (
          <form onSubmit={handleRegister} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Your Full Name</label><input className="lp-inp" required autoFocus value={regData.adminName} onChange={(event) => setR('adminName', event.target.value)} placeholder="e.g. N. S. Mehta" /></div>
            <div className="lp-fg"><label className="lp-lbl">Your Email</label><input className="lp-inp" type="email" required value={regData.adminEmail} onChange={(event) => setR('adminEmail', event.target.value)} placeholder="you@firm.com" /></div>
            <div className="lp-grid2">
              <div className="lp-fg"><label className="lp-lbl">Password</label><input className="lp-inp" type="password" required value={regData.password} onChange={(event) => setR('password', event.target.value)} placeholder="Min 12 chars" /></div>
              <div className="lp-fg"><label className="lp-lbl">Confirm</label><input className="lp-inp" type="password" required value={regData.confirmPassword} onChange={(event) => setR('confirmPassword', event.target.value)} placeholder="Repeat" /></div>
            </div>
            <p className="lp-auth-note">{buildPasswordPolicyText(authConfig.passwordPolicy)}</p>
            <button className="lp-submit" type="submit">Continue →</button>
            <p className="lp-auth-switch">Already registered? <button type="button" onClick={() => switchTab('login')}>Sign in</button></p>
          </form>
        )}

        {tab === 'register' && step === 2 && (
          <form onSubmit={handleRegister} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Firm / Company Name *</label><input className="lp-inp" required autoFocus value={regData.firmName} onChange={(event) => setR('firmName', event.target.value)} placeholder="e.g. Triyog Innovations Pvt Ltd" /></div>
            <div className="lp-fg"><label className="lp-lbl">Official Firm Email (optional)</label><input className="lp-inp" type="email" value={regData.firmEmail} onChange={(event) => setR('firmEmail', event.target.value)} placeholder="info@firm.com" /></div>
            <p className="lp-auth-note">You can add logo, GST/PAN, bank details, and signatories after registration from Settings.</p>
            {authConfig.registrationOpen && authConfig.turnstileEnabled && (
              <div className="lp-fg">
                <label className="lp-lbl">Security Verification</label>
                <TurnstileWidget
                  siteKey={authConfig.turnstileSiteKey}
                  resetKey={captchaResetKey}
                  onTokenChange={setCaptchaToken}
                  onError={setError}
                />
              </div>
            )}
            {!authConfig.registrationOpen && (
              <div className="lp-auth-error">
                Secure registration is temporarily unavailable until CAPTCHA keys are configured.
              </div>
            )}
            <div className="lp-grid2">
              <button type="button" className="lp-back-btn" onClick={() => { setStep(1); setError(''); }}>← Back</button>
              <button className="lp-submit" type="submit" disabled={loading || !authConfig.registrationOpen}>{loading ? 'Creating…' : 'Create Firm →'}</button>
            </div>
          </form>
        )}
      </div>
      <div className="lp-auth-foot">SSL encrypted · CAPTCHA-protected registration · No credit card required</div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-faq-item">
      <button className="lp-faq-q" onClick={() => setOpen((value) => !value)}>
        {q}
        <span className={`lp-faq-chevron${open ? ' open' : ''}`}>⌄</span>
      </button>
      {open && <div className="lp-faq-a">{a}</div>}
    </div>
  );
}

function TurnstileWidget({ siteKey, resetKey, onTokenChange, onError }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey || !containerRef.current) return undefined;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        containerRef.current.innerHTML = '';
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          action: 'register_firm',
          callback: (token) => onTokenChange(token),
          'expired-callback': () => onTokenChange(''),
          'error-callback': () => {
            onTokenChange('');
            onError?.('Security verification could not be completed. Please retry.');
          },
        });
      })
      .catch(() => {
        if (!cancelled) onError?.('Security verification could not be loaded. Please retry.');
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile?.remove) {
        try { window.turnstile.remove(widgetIdRef.current); } catch {}
      }
    };
  }, [siteKey, resetKey, onTokenChange, onError]);

  return <div className="lp-turnstile-box"><div ref={containerRef} /></div>;
}

export default function AuthScreen({ onAuth }) {
  const heroRef = useRef(null);
  const authRef = useRef(null);
  const featuresId = 'lp-features';
  const howId = 'lp-how';
  const industriesId = 'lp-industries';
  const [authConfig, setAuthConfig] = useState(DEFAULT_AUTH_CONFIG);
  const [authIntent, setAuthIntent] = useState({ tab: 'login', step: 1, version: 0 });

  useEffect(() => {
    document.title = SEO_TITLE;
    injectMeta('name', 'description', SEO_DESC);
    injectMeta('name', 'keywords', SEO_KEYS);
    injectMeta('name', 'robots', 'index, follow, max-image-preview:large');
    injectMeta('property', 'og:type', 'website');
    injectMeta('property', 'og:title', SEO_TITLE);
    injectMeta('property', 'og:description', SEO_DESC);
    injectMeta('property', 'og:url', `${SITE_URL}/`);
    injectMeta('property', 'og:site_name', 'e-FinPro');
    injectMeta('property', 'og:image', `${SITE_URL}/efinpro-social.svg`);
    injectMeta('property', 'og:image:alt', 'e-FinPro contract, invoice, TDS and reporting SaaS');
    injectMeta('name', 'twitter:card', 'summary_large_image');
    injectMeta('name', 'twitter:title', SEO_TITLE);
    injectMeta('name', 'twitter:description', SEO_DESC);
    injectMeta('name', 'twitter:image', `${SITE_URL}/efinpro-social.svg`);
    injectMeta('name', 'twitter:url', `${SITE_URL}/`);
    injectCanonical(`${SITE_URL}/`);
    injectLD({
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'WebSite', name: 'e-FinPro', url: `${SITE_URL}/`, description: SEO_DESC, inLanguage: 'en-IN' },
        {
          '@type': 'Organization', name: 'e-FinPro', url: `${SITE_URL}/`,
          logo: `${SITE_URL}/efinpro_logo.svg`,
          sameAs: [`${SITE_URL}/`],
        },
        {
          '@type': 'SoftwareApplication', name: 'e-FinPro',
          applicationCategory: 'BusinessApplication', operatingSystem: 'Web',
          url: `${SITE_URL}/`, description: SEO_DESC,
          featureList: [
            'Contract management with milestones and deliverables',
            'GST invoice generation (IGST/CGST)',
            'TDS-aware receipt tracking',
            'Bank ledger with running balance',
            'Tally XML export (Sales Voucher)',
            'Invoice register and GST/TDS report exports',
            'Multi-firm isolated workspaces',
            'Multi-currency billing',
          ],
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'INR',
            description: 'Free firm workspace registration — no credit card required.',
          },
        },
        { '@type': 'FAQPage', mainEntity: FAQS.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })) },
      ],
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    authAPI.publicConfig()
      .then((config) => {
        if (!cancelled) setAuthConfig({ ...DEFAULT_AUTH_CONFIG, ...config });
      })
      .catch(() => {
        if (!cancelled) setAuthConfig(DEFAULT_AUTH_CONFIG);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  const focusAuth = (tab = 'login', step = 1) => {
    setAuthIntent({ tab, step, version: Date.now() });
    const target = window.innerWidth > 980 ? heroRef.current : authRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="lp-root">
      <nav className="lp-nav">
        <div className="container-xxl px-0 lp-nav-shell">
          <div className="lp-nav-logo">
            <img src="/efinpro_logo.svg" alt="e-FinPro" />
          </div>
          <div className="lp-nav-links">
            <button className="lp-nav-link" onClick={() => scrollTo(featuresId)}>Features</button>
            <button className="lp-nav-link" onClick={() => scrollTo(howId)}>How it works</button>
            <button className="lp-nav-link" onClick={() => scrollTo(industriesId)}>Who it's for</button>
          </div>
          <div className="lp-nav-ctas">
            <button className="lp-btn-ghost" onClick={() => focusAuth('login', 1)}>Sign In</button>
            <button className="lp-btn-gold" onClick={() => focusAuth('register', 1)}>Get Started Free</button>
          </div>
        </div>
      </nav>

      <section className="lp-hero" ref={heroRef}>
        <div className="container-xxl px-0 lp-hero-shell">
          <div className="row g-4 g-xl-5 align-items-center">
            <div className="col-xl-7">
              <div className="lp-hero-left">
                <div className="lp-eyebrow"><span className="lp-eyebrow-dot" />Contract-to-Cash · India-Ready</div>

                <h1 className="lp-h1">
                  Run contract-led finance
                  <br />
                  without <em>spreadsheet sprawl.</em>
                </h1>

                <p className="lp-hero-sub">
                  e-FinPro gives NGOs, PMUs, and consulting firms one operating layer for agreements, GST invoices, TDS receipts, bank movement, and Tally-ready exports.
                </p>

                <div className="lp-hero-signal-row">
                  {HERO_SIGNALS.map((signal) => (
                    <div key={signal.label} className="lp-hero-signal">
                      <span className="lp-hero-signal-value">{signal.value}</span>
                      <span className="lp-hero-signal-label">{signal.label}</span>
                    </div>
                  ))}
                </div>

                <div className="row g-3 lp-hero-module-grid">
                  {HERO_MODULES.map((module) => (
                    <div key={module.title} className="col-sm-6 col-xl-4">
                      <div className="lp-hero-module">
                        <div className={`lp-hero-module-icon ${module.cls}`}>{module.icon}</div>
                        <h3 className="lp-hero-module-title">{module.title}</h3>
                        <p className="lp-hero-module-body">{module.body}</p>
                        <div className="lp-hero-module-kicker">{module.kicker}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="lp-hero-ctas">
                  <button className="lp-cta-primary" onClick={() => focusAuth('register', 1)}>
                    Start Free — No Card Needed
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                  <button className="lp-cta-secondary" onClick={() => scrollTo(howId)}>
                    See how it works
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9" /></svg>
                  </button>
                </div>

                <div className="lp-hero-trust">
                  <span className="lp-hero-trust-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    SSL Encrypted
                  </span>
                  <span className="lp-hero-trust-sep">·</span>
                  <span className="lp-hero-trust-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                    GST &amp; TDS Compliant
                  </span>
                  <span className="lp-hero-trust-sep">·</span>
                  <span className="lp-hero-trust-item">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="20 6 9 17 4 12" /></svg>
                    Free to start
                  </span>
                </div>
              </div>
            </div>

            <div className="col-xl-5">
              <div className="lp-hero-right" ref={authRef}>
                <div className="lp-dpk" aria-hidden="true">
                  <div className="lp-dpk-bar">
                    <div className="lp-dpk-dots">
                      <span className="lp-dpk-dot lp-dpk-dot-red" />
                      <span className="lp-dpk-dot lp-dpk-dot-amber" />
                      <span className="lp-dpk-dot lp-dpk-dot-green" />
                    </div>
                    <span className="lp-dpk-label">e-FinPro · Workspace</span>
                    <span className="lp-dpk-live"><span className="lp-dpk-live-dot" />Live</span>
                  </div>
                  <div className="lp-dpk-stats">
                    {[
                      { val: 'Rs 45.2L', lbl: 'Total contracts', clr: 'var(--blue-glow)' },
                      { val: 'Rs 38.7L', lbl: 'Invoiced', clr: 'var(--gold)' },
                      { val: 'Rs 3.9L', lbl: 'TDS tracked', clr: 'var(--green-glow)' },
                    ].map((item, index) => (
                      <div key={index} className="lp-dpk-stat">
                        <div className="lp-dpk-val" style={{ color: item.clr }}>{item.val}</div>
                        <div className="lp-dpk-lbl">{item.lbl}</div>
                      </div>
                    ))}
                  </div>
                  <div className="lp-dpk-row">
                    {[
                      { dot: 'lp-hp-dot-green', label: 'QR-Based Registration Agreement', val: 'Rs 9,73,000' },
                      { dot: 'lp-hp-dot-gold', label: 'Invoice #INV/26-27/2', val: 'Rs 9,72,101' },
                    ].map((row, index) => (
                      <div key={index} className="lp-dpk-item">
                        <div className={`lp-hp-dot ${row.dot}`} style={{ width: 6, height: 6 }} />
                        <span className="lp-dpk-item-label">{row.label}</span>
                        <span className="lp-dpk-item-val">{row.val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <AuthForm onAuth={onAuth} authConfig={authConfig} authIntent={authIntent} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="lp-trust-bar" aria-label="Built for">
        <span className="lp-trust-label">Built for</span>
        <div className="lp-trust-chips">
          {['NGO Finance', 'PMU Billing', 'Consulting Contracts', 'Donor-Funded Programmes', 'Government Work Orders', 'Multi-Vendor Projects'].map((text) => (
            <span key={text} className="lp-trust-chip">{text}</span>
          ))}
        </div>
      </div>

      <div className="lp-stats-bar" aria-label="Platform highlights">
        {STATS.map((stat) => (
          <div key={stat.label} className="lp-stat">
            <div className="lp-stat-num">{stat.num}</div>
            <div className="lp-stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <section className="lp-section lp-section-soft" id={featuresId}>
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Platform Capabilities</div>
          <h2 className="lp-section-title">Everything a contract-led finance team needs</h2>
          <p className="lp-section-sub">Manage the complete billing lifecycle from agreement signing to receipt recording and reporting — without switching tools.</p>
        </div>
        <div className="lp-features-grid">
          {FEATURES.map((feature) => (
            <div key={feature.title} className="lp-feature-card">
              <div className={`lp-feature-icon ${feature.cls}`}>{feature.icon}</div>
              <h3 className="lp-feature-title">{feature.title}</h3>
              <p className="lp-feature-body">{feature.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section-white" id={howId}>
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">The Workflow</div>
          <h2 className="lp-section-title">Contract → Invoice → Receipt → Report</h2>
          <p className="lp-section-sub">A linear, connected workflow so every invoice traces back to an agreement and every receipt closes the loop.</p>
        </div>
        <div className="lp-steps-grid">
          {WORKFLOW.map((item) => (
            <div key={item.n} className="lp-step-card">
              <div className="lp-step-num-circle">{item.n}</div>
              <h3 className="lp-step-title">{item.title}</h3>
              <p className="lp-step-body">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="lp-workflow lp-section-soft">
        <div>
          <div className="lp-section-eyebrow">Why it matters</div>
          <h2 className="lp-workflow-title">One record, from agreement to settlement</h2>
          <p className="lp-workflow-sub">Most teams track contracts in one tool, invoices in another, and receipts in a spreadsheet. e-FinPro connects all three so finance teams get a single, auditable source of truth.</p>
          <div className="lp-workflow-list">
            {['Contract ceiling and invoiced amount stay in sync in real time.', 'TDS deductions record against each receipt — not estimated at year end.', 'Ledger reflects actual bank movement, not manual data entry.', 'Reports pull from live data — not from a rebuilt spreadsheet.'].map((item, index) => (
              <div key={index} className="lp-workflow-item">
                <div className="lp-workflow-item-num">{index + 1}</div>
                <div className="lp-workflow-item-text">{item}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-workflow-visual">
          <div className="lp-wv-cards">
            {[
              { dot: 'lp-wv-dot-green', label: 'QR-Based Registration Agreement', val: 'Rs 9,73,000', badge: 'lp-wv-badge-green', btext: 'Active' },
              { dot: 'lp-wv-dot-gold', label: 'Invoice #INV/26-27/2', val: 'Rs 9,72,101', badge: 'lp-wv-badge-gold', btext: 'Pending' },
              { dot: 'lp-wv-dot-blue', label: 'Receipt recorded — TDS Rs 97,210', val: 'Rs 8,74,891', badge: 'lp-wv-badge-blue', btext: 'Settled' },
              { dot: 'lp-wv-dot-green', label: 'Bank Balance', val: 'Rs 8,47,738', badge: 'lp-wv-badge-green', btext: 'Live' },
            ].map((row, index) => (
              <div key={index} className="lp-wv-card">
                <div className={`lp-wv-dot ${row.dot}`} />
                <span className="lp-wv-label">{row.label}</span>
                <span className="lp-wv-val">{row.val}</span>
                <span className={`lp-wv-badge ${row.badge}`}>{row.btext}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <section className="lp-section lp-section-dark" id={industriesId}>
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">Built For</div>
          <h2 className="lp-section-title">Who uses e-FinPro</h2>
          <p className="lp-section-sub">Purpose-built for teams where contracts drive billing and every receipt has to be accounted for.</p>
        </div>
        <div className="lp-industry-grid">
          {INDUSTRIES.map((industry) => (
            <div key={industry.title} className="lp-industry-card">
              <div className="lp-industry-icon" role="img" aria-label={industry.title}>{industry.icon}</div>
              <h3 className="lp-industry-title">{industry.title}</h3>
              <p className="lp-industry-body">{industry.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-section lp-section-soft">
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">FAQ</div>
          <h2 className="lp-section-title">Common questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map((faq) => <FaqItem key={faq.q} {...faq} />)}
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="lp-section lp-section-white lp-contact-section" id="lp-contact">
        <div className="container-xxl px-0">
          <div className="row g-4 align-items-stretch">
            <div className="col-lg-5">
              <div className="lp-contact-lead">
                <div className="lp-section-eyebrow">Contact</div>
                <h2 className="lp-section-title">Need a guided setup instead of a blank form?</h2>
                <p className="lp-section-sub">Have a question, need a walkthrough, or want to shape your firm workspace before you invite the team? We can help you get the first setup right.</p>
                <div className="lp-contact-points">
                  {CONTACT_POINTS.map((point) => (
                    <div key={point} className="lp-contact-point">
                      <div className="lp-contact-point-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><polyline points="20 6 9 17 4 12" /></svg>
                      </div>
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="row g-3 h-100">
                <div className="col-md-6">
                  <a className="lp-contact-card lp-contact-card-link h-100" href="mailto:info@triyoginnovation.com">
                    <div className="lp-contact-top">
                      <div className="lp-contact-icon lp-ci-blue">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22 6 12 13 2 6" />
                        </svg>
                      </div>
                      <div className="lp-contact-body">
                        <div className="lp-contact-label">Email support</div>
                        <div className="lp-contact-val">info@triyoginnovation.com</div>
                      </div>
                    </div>
                    <p className="lp-contact-copy">Reach the team for onboarding questions, pricing clarity, or a walkthrough of the contract-to-cash workflow.</p>
                    <div className="lp-contact-meta">Reply within one business day</div>
                    <div className="lp-contact-linkrow">
                      Send email
                      <svg className="lp-contact-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </div>
                  </a>
                </div>

                <div className="col-md-6">
                  <button className="lp-contact-card lp-contact-card-button h-100" type="button" onClick={() => focusAuth('register', 1)}>
                    <div className="lp-contact-top">
                      <div className="lp-contact-icon lp-ci-gold">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                          <path d="M12 20h9" />
                          <path d="M12 4h9" />
                          <path d="M4 9h16" />
                          <path d="M4 15h10" />
                          <path d="M7 4v16" />
                        </svg>
                      </div>
                      <div className="lp-contact-body">
                        <div className="lp-contact-label">Workspace setup</div>
                        <div className="lp-contact-val">Start with a firm that is configured cleanly</div>
                      </div>
                    </div>
                    <p className="lp-contact-copy">Register your workspace and add the firm details, signatories, GST rules, and finance structure you want from day one.</p>
                    <div className="lp-contact-meta">Best for first-time setup</div>
                    <div className="lp-contact-linkrow">
                      Open registration
                      <svg className="lp-contact-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                    </div>
                  </button>
                </div>

                <div className="col-12">
                  <div className="lp-contact-band">
                    <div className="lp-contact-band-copy">
                      <strong>Triyog Innovations Pvt. Ltd.</strong> builds e-FinPro for India-focused finance teams that need contracts, GST, TDS, and reporting to stay connected instead of scattered across tools.
                    </div>
                    <button className="lp-contact-band-btn" type="button" onClick={() => scrollTo(featuresId)}>
                      Explore platform modules
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lp-cta-section">
        <h2 className="lp-cta-h2">Start managing contracts the right way</h2>
        <p className="lp-cta-sub">Register your firm workspace in 2 minutes — no credit card, no setup fee. Add GST, team members, and signatories when you're ready.</p>
        <div className="lp-cta-btns">
          <button className="lp-cta-big lp-cta-big-gold" onClick={() => focusAuth('register', 1)}>Register Free →</button>
          <button className="lp-cta-big lp-cta-big-outline" onClick={() => scrollTo(featuresId)}>Explore Features</button>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-footer-brand">
          <img src="/triyog-logo.svg" alt="e-FinPro" className="lp-footer-logo" />
          <span className="lp-footer-name">e-FinPro</span>
        </div>
        <span className="lp-footer-copy">© {new Date().getFullYear()} Triyog Innovations Private Limited. All rights reserved.</span>
        <div className="lp-footer-links">
          <span className="lp-footer-link">Contract-to-cash SaaS for India</span>
          <span className="lp-footer-link">GST · TDS · Tally · Multi-firm</span>
          <a className="lp-footer-link" href="mailto:info@triyoginnovation.com">info@triyoginnovation.com</a>
        </div>
      </footer>
    </div>
  );
}
